import { gql, QueryHookOptions, useMutation, useQuery } from '@apollo/client';
import { Label, LabelGroup } from '@provision/entity-types';
import { useCallback, useMemo } from 'react';
import { isNullish } from 'remeda';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { ConditionInput, QueryVariables, Records } from 'src/graphql/types';
import { useNotifications } from 'src/utils/hooks';
import { sortByDate } from 'src/utils/sorting';
import { LABEL_FIELDS } from './label/label.fragments';

const LABEL_GROUP_FIELDS = gql`
  fragment LabelGroupFields on LabelGroup {
    id
    labelGroupId
    displayName
    partnerId
  }
`;

const GET_LABEL_GROUPS_QUERY = gql`
  query GetLabelGroup($params: QueryParams) @entity(name: "LabelGroup") {
    getLabelGroup(queryParams: $params) {
      records {
        ...LabelGroupFields

        labels {
          ...LabelFields
        }
      }
    }
  }
  ${LABEL_GROUP_FIELDS}
  ${LABEL_FIELDS}
`;

export const CREATE_LABEL_GROUP_MUTATION = gql`
  mutation CreateLabelGroup($data: LabelGroupDataInput) {
    createLabelGroup(data: $data) {
      ...LabelGroupFields
    }
  }
  ${LABEL_GROUP_FIELDS}
`;

const UPDATE_LABEL_GROUP_MUTATION = gql`
  mutation UpdateLabelGroup($conditions: [ConditionInput], $data: LabelGroupDataInput) {
    updateLabelGroup(conditions: $conditions, data: $data) {
      ...LabelGroupFields
    }
  }
  ${LABEL_GROUP_FIELDS}
`;

const DELETE_LABEL_GROUP_MUTATION = gql`
  mutation DeleteLabelGroup($labelGroupId: String!) {
    deleteLabelGroup(
      conditions: [{ attribute: "labelGroupId", operator: "=", value: $labelGroupId }]
    ) {
      ...LabelGroupFields
    }
  }
  ${LABEL_GROUP_FIELDS}
`;

export const useLabelGroup = (
  options?: QueryHookOptions<GetLabelGroups> & {
    desc?: boolean;
    filters?: ConditionInput[];
  },
) => {
  const { desc = true, filters = [] } = options ?? {};

  const { showErrorAlert } = useNotifications();
  const [updateLabelGroupMutation] = useMutation<{
    updateLabelGroup: LabelGroup[];
  }>(UPDATE_LABEL_GROUP_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert('Something went wrong updating the label group, please try again');
    },
  });

  const updateLabelGroup = useCallback(
    (labelGroup: LabelGroup) => {
      const patch = {
        displayName: labelGroup.displayName,
      };
      if (!labelGroup.labelGroupId) {
        return;
      }

      return updateLabelGroupMutation({
        variables: {
          conditions: [
            {
              attribute: 'labelGroupId',
              operator: '=',
              value: labelGroup.labelGroupId,
            },
          ],
          data: patch,
        },
        optimisticResponse: {
          updateLabelGroup: [
            {
              ...labelGroup,
              ...patch,
            },
          ],
        },
      });
    },
    [updateLabelGroupMutation],
  );

  const [deleteLabelGroupMutation] = useMutation<{
    deleteLabelGroup: {
      labelGroupId: string;
    }[];
  }>(DELETE_LABEL_GROUP_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert('Something went wrong deleting the group label, please try again');
    },
  });

  const deleteLabelGroup = useCallback(
    (labelGroup: LabelGroup) => {
      return deleteLabelGroupMutation({
        variables: { labelGroupId: labelGroup.labelGroupId },
        optimisticResponse: {
          deleteLabelGroup: [
            {
              labelGroupId: labelGroup.labelGroupId,
            },
          ],
        },
        update(cache, { data }) {
          if (!data?.deleteLabelGroup?.length) return;
          if (!data.deleteLabelGroup[0].labelGroupId) return;

          const id = cache.identify({
            __typename: 'LabelGroup',
            id: labelGroup.labelGroupId,
          });

          cache.evict({ id });
          cache.gc();
        },
      });
    },
    [deleteLabelGroupMutation],
  );

  const [createLabelGroupMutation] = useMutation<{
    createLabelGroup: {
      id: string;
      labels: Label[];
      labelGroupId: string;
      displayName: string;
      partnerId?: number | null;
      createdAt?: string;
      updatedAt?: string;
    }[];
  }>(CREATE_LABEL_GROUP_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert('Something went wrong creating a new label group, please try again');
    },
  });

  const createLabelGroup = useCallback(
    async (labelGroup: Partial<LabelGroup>, partnerId?: number | null) => {
      const createdAt = new Date().toISOString();
      const tempId = `temp-id${createdAt}`;

      const { data } = await createLabelGroupMutation({
        variables: {
          data: {
            displayName: labelGroup.displayName,
            partnerId,
          },
        },
        optimisticResponse: {
          createLabelGroup: [
            {
              id: labelGroup.labelGroupId || tempId,
              labelGroupId: labelGroup.labelGroupId || tempId,
              labels: labelGroup.labels || [],
              displayName: labelGroup.displayName ?? '',
              partnerId,
              createdAt,
            },
          ],
        },
        update(cache, { data }) {
          if (!data?.createLabelGroup?.length) return;
          if (!data.createLabelGroup[0].labelGroupId) return;

          cache.updateQuery<GetLabelGroups, QueryVariables>(
            {
              query: GET_LABEL_GROUPS_QUERY,
              variables: {
                params: {
                  filters,
                  sorting: { attribute: 'updatedAt', desc },
                },
              },
            },
            (existingData) => {
              if (!existingData) return;

              return {
                getLabelGroup: {
                  records: [
                    {
                      ...data.createLabelGroup[0],
                      labels: [] as Label[],
                    } as LabelGroup,
                    ...existingData.getLabelGroup.records,
                  ],
                },
              };
            },
          );
        },
      });

      return data?.createLabelGroup;
    },
    [createLabelGroupMutation, desc, filters],
  );

  return {
    updateLabelGroup,
    createLabelGroup,
    deleteLabelGroup,
  };
};

export interface GetLabelGroups {
  getLabelGroup: Records<LabelGroup>;
}

export const useLabelGroups = (
  params: {
    partnerId?: number | null;
  },
  options?: QueryHookOptions<GetLabelGroups> & {
    desc?: boolean;
    filters?: ConditionInput[];
  },
) => {
  const { desc = true, filters = [], skip = false } = options ?? {};

  const initialFilters = [];
  if (!isNullish(params.partnerId)) {
    initialFilters.push(
      // This filter should be present, since it is also used in multi-tenancy mode
      // when partner-id header is not included in the request.
      {
        attribute: 'partnerId',
        operator: 'in',
        value: [params.partnerId, PLATFORM_PARTNER_ID].join(','),
      },
    );
  }

  const { data, ...rest } = useQuery<GetLabelGroups, QueryVariables>(GET_LABEL_GROUPS_QUERY, {
    ...options,
    context: {
      includePlatformLevelData: true,
    },
    skip: skip || isNullish(params.partnerId),
    variables: {
      params: {
        filters: [...initialFilters, ...filters],
        sorting: { attribute: 'updatedAt', desc },
      },
    },
  });

  const labelGroups = useMemo(
    () =>
      data?.getLabelGroup?.records.map((labelGroup) => ({
        ...labelGroup,
        labels: [...labelGroup.labels].sort(sortByDate('updatedAt', true)),
      })) || [],
    [data?.getLabelGroup?.records],
  );

  const operations = useLabelGroup({
    filters: [...initialFilters, ...filters],
  });

  return {
    labelGroups,
    ...operations,
    ...rest,
  };
};
